import React from 'react';
import { X, FileText, Lock } from 'lucide-react';

interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'terms' | 'privacy';
}

export const LegalModal: React.FC<LegalModalProps> = ({ isOpen, onClose, type }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl border border-medical-200 w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-medical-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            {type === 'terms' ? (
              <FileText className="h-5 w-5 text-primary-600" />
            ) : (
              <Lock className="h-5 w-5 text-primary-600" />
            )}
            <h2 className="text-xl font-semibold text-medical-900">
              {type === 'terms' ? 'Terms of Service' : 'Privacy Policy'}
            </h2>
            <span className="text-xs bg-medical-100 text-medical-600 px-2 py-0.5 rounded-full">v1.0</span>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-medical-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-medical-500" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto px-8 py-6 flex-1 prose prose-sm prose-medical max-w-none">
          {type === 'terms' ? <TermsContent /> : <PrivacyContent />}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-8 py-4 border-t border-medical-200 flex-shrink-0">
          <button
            onClick={onClose}
            className="btn-primary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-base font-semibold text-medical-900 mt-6 mb-2">{children}</h3>
);

const SubHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h4 className="text-sm font-semibold text-medical-800 mt-4 mb-1">{children}</h4>
);

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-sm text-medical-700 mb-3 leading-relaxed">{children}</p>
);

const BulletList: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="list-disc list-inside text-sm text-medical-700 mb-3 space-y-1 ml-2">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

export const TermsContent: React.FC = () => (
  <div>
    <P>
      <strong>Effective Date:</strong> 26 March 2026 &nbsp;|&nbsp; <strong>Version:</strong> 1.0
    </P>
    <P>
      These Terms of Service ("Terms") govern your access to and use of the ClinLexis platform
      ("Service"), operated by ClinLexis Ltd ("we", "us", "our"), a company registered in
      England and Wales (Company No. 17081483), with its registered office at 238a Telegraph Road,
      Heswall, Wirral, CH60 0AL. By creating an account you agree to be bound by these Terms.
    </P>

    <SectionHeading>1. Definitions</SectionHeading>
    <BulletList items={[
      '"Platform" means the ClinLexis web application, APIs, and associated services.',
      '"User" means any individual who creates an account on the Platform.',
      '"Content" means any documents, data, text, images, or other materials uploaded to or generated by the Platform.',
      '"AI Processing" means automated analysis of Content using artificial intelligence and machine learning models.',
    ]} />

    <SectionHeading>2. Eligibility and Account Registration</SectionHeading>
    <P>
      The Service is intended for use by qualified healthcare professionals, medical legal professionals,
      and authorised personnel within healthcare organisations. By registering, you represent that you are
      at least 18 years of age and have the legal authority to enter into these Terms.
    </P>
    <P>
      You are responsible for maintaining the confidentiality of your account credentials and for all
      activities that occur under your account. You must notify us immediately of any unauthorised use.
    </P>

    <SectionHeading>3. Description of the Service</SectionHeading>
    <P>
      ClinLexis provides AI-assisted medical document management, chronology generation,
      evidence analysis, and reporting tools. The Service is designed to assist healthcare professionals
      in organising and analysing clinical documentation.
    </P>
    <P>
      <strong>The Service does not provide medical advice.</strong> All AI-generated outputs are intended
      as assistive tools and must be reviewed and validated by qualified professionals before being relied
      upon for clinical or legal decisions.
    </P>

    <SectionHeading>4. Acceptable Use</SectionHeading>
    <P>You agree to use the Service only for lawful purposes and in accordance with these Terms. You must not:</P>
    <BulletList items={[
      'Upload content that you do not have the legal right to process.',
      'Use the Service to process data in violation of applicable data protection laws.',
      'Attempt to gain unauthorised access to the Service or its underlying systems.',
      'Use the Service in any way that could damage, disable, or impair its operation.',
      'Reverse-engineer, decompile, or disassemble any part of the Service.',
      'Share your account credentials with third parties.',
      'Use automated tools or scripts to access the Service without our prior written consent.',
    ]} />

    <SectionHeading>5. Data Ownership and Intellectual Property</SectionHeading>
    <P>
      You retain all rights to Content that you upload to the Platform. By uploading Content, you
      grant us a limited licence to process, store, and analyse that Content solely for the purpose
      of providing the Service to you.
    </P>
    <P>
      The Platform, including its design, code, algorithms, and documentation, is the intellectual
      property of ClinLexis Ltd and is protected by copyright, trade mark, and other
      intellectual property laws.
    </P>

    <SectionHeading>6. AI Processing and Limitations</SectionHeading>
    <P>
      The Service uses artificial intelligence to process uploaded documents. You acknowledge that:
    </P>
    <BulletList items={[
      'AI-generated outputs may contain errors and should always be reviewed by a qualified professional.',
      'The Service is an assistive tool and does not replace professional clinical or legal judgement.',
      'We continuously work to improve accuracy but cannot guarantee error-free results.',
      'AI models may be updated from time to time, which may affect output characteristics.',
    ]} />
    <SubHeading>6.1 AI Feature Availability Window</SubHeading>
    <P>
      AI-powered features — including the Ask Assistant, report generation, analysis regeneration,
      and any other functionality that relies on AI processing of your case data — are available for
      a period of ninety (90) days from the date on which the initial analysis for a given case is
      generated (the "AI Availability Window").
    </P>
    <P>
      After the AI Availability Window expires:
    </P>
    <BulletList items={[
      'You will retain full access to all previously generated outputs, including chronologies, analysis reports, key findings, and any user-edited content.',
      'You will no longer be able to use AI-powered features for that case, including the Ask Assistant, report generation, or analysis regeneration.',
      'Underlying AI processing data (such as document embeddings and conversational history) may be permanently deleted to manage platform resources.',
      'To restore AI features for an expired case, you may re-run the analysis pipeline, which will consume credits from your account.',
    ]} />
    <P>
      The AI Availability Window applies on a per-case basis. Each case's window begins independently
      when its analysis is first generated. We will notify you via the Platform when a case's AI
      Availability Window is approaching expiry.
    </P>

    <SectionHeading>7. Subscription, Credits, and Payment</SectionHeading>
    <P>
      The Service operates on a credit-based model. Credits are consumed when documents are processed
      and are measured in pages. Credits do not roll over between billing periods.
    </P>
    <SubHeading>7.1 Free Trial and Promotional Credits</SubHeading>
    <P>
      We may offer free credits through invite codes or promotional offers. Free credits grant access
      to the Service on the same basis as paid credits but do not constitute a subscription. When free
      credits are exhausted, you will no longer be able to process new documents. You may continue to
      view existing cases and data for a period of ninety (90) days after your last credit is consumed
      or your last login, whichever is later (the "Retention Period"). After the Retention Period, we
      reserve the right to delete your data in accordance with Section 11.
    </P>
    <SubHeading>7.2 Paid Subscriptions</SubHeading>
    <P>
      Paid subscription fees are charged in advance on a monthly basis. All fees are quoted in British
      Pounds (GBP) and are exclusive of VAT where applicable. Each billing cycle, your credit balance
      is reset to your plan's monthly allowance.
    </P>
    <P>
      Paid subscribers who exceed their monthly credit allowance may incur overage charges at the
      per-page rate applicable to their plan, up to a maximum of twice the monthly allowance.
    </P>
    <SubHeading>7.3 Cancellation</SubHeading>
    <P>
      You may cancel your subscription at any time. Cancellation takes effect at the end of the current
      billing period. We do not provide refunds for partial billing periods. Upon cancellation, the
      Retention Period described in Section 7.1 applies.
    </P>

    <SectionHeading>8. Data Protection and Security</SectionHeading>
    <P>
      We process personal data in accordance with our Privacy Policy and applicable data protection
      legislation, including the UK General Data Protection Regulation (UK GDPR) and the Data
      Protection Act 2018. We implement appropriate technical and organisational measures to protect
      your data.
    </P>

    <SectionHeading>9. Service Availability and Support</SectionHeading>
    <P>
      We aim to provide a reliable service but do not guarantee uninterrupted access. We may
      temporarily suspend the Service for maintenance, updates, or security reasons. We will
      provide reasonable notice of planned downtime where practicable.
    </P>

    <SectionHeading>10. Limitation of Liability</SectionHeading>
    <P>
      To the maximum extent permitted by law, ClinLexis Ltd shall not be liable for any
      indirect, incidental, special, consequential, or punitive damages arising from your use of
      the Service, including but not limited to loss of data, loss of profits, or clinical outcomes
      based on AI-generated outputs.
    </P>
    <P>
      Our total aggregate liability shall not exceed the fees paid by you in the twelve (12) months
      preceding the event giving rise to the claim.
    </P>

    <SectionHeading>11. Termination and Data Retention</SectionHeading>
    <P>
      We may suspend or terminate your access to the Service if you breach these Terms, fail to pay
      subscription fees, or if we are required to do so by law.
    </P>
    <SubHeading>11.1 Data Retention After Termination or Expiry</SubHeading>
    <P>
      Following cancellation, termination, or exhaustion of credits without an active subscription,
      your uploaded Content and account data will be retained for ninety (90) days (the "Retention
      Period"). During the Retention Period, you may log in to view your existing data and export it
      in accordance with our data portability obligations under GDPR.
    </P>
    <P>
      We will send email notifications at thirty (30) days and seven (7) days before the end of the
      Retention Period to remind you to export your data. After the Retention Period expires, we
      reserve the right to permanently delete all Content associated with your account. Deleted data
      cannot be recovered.
    </P>
    <SubHeading>11.2 Exceptions</SubHeading>
    <P>
      We may retain certain data beyond the Retention Period where required by law, for the
      establishment, exercise, or defence of legal claims, or for GDPR compliance audit purposes
      (e.g. consent records, which are retained for 7 years).
    </P>

    <SectionHeading>12. Changes to These Terms</SectionHeading>
    <P>
      We may update these Terms from time to time. We will notify you of material changes via email
      or through the Platform at least 30 days before they take effect. Continued use of the Service
      after changes take effect constitutes acceptance of the updated Terms.
    </P>

    <SectionHeading>13. Governing Law</SectionHeading>
    <P>
      These Terms are governed by and construed in accordance with the laws of England and Wales.
      Any disputes arising from these Terms shall be subject to the exclusive jurisdiction of the
      courts of England and Wales.
    </P>

    <SectionHeading>14. Contact</SectionHeading>
    <P>
      If you have questions about these Terms, please contact us at{' '}
      <strong>support@clinlexis.co.uk</strong>.
    </P>
  </div>
);

export const PrivacyContent: React.FC = () => (
  <div>
    <P>
      <strong>Effective Date:</strong> 26 March 2026 &nbsp;|&nbsp; <strong>Version:</strong> 1.0
    </P>
    <P>
      This Privacy Policy explains how ClinLexis Ltd ("we", "us", "our") collects, uses,
      stores, and protects personal data when you use the ClinLexis platform ("Service").
      We are committed to protecting your privacy and complying with the UK General Data Protection
      Regulation (UK GDPR) and the Data Protection Act 2018.
    </P>

    <SectionHeading>1. Data Controller</SectionHeading>
    <P>
      ClinLexis Ltd (Company No. 17081483), with its registered office at 238a Telegraph Road,
      Heswall, Wirral, CH60 0AL, is the data controller for the personal data described in this policy.
      Our Data Protection Officer can be contacted at <strong>support@clinlexis.co.uk</strong>.
    </P>

    <SectionHeading>2. Personal Data We Collect</SectionHeading>
    <SubHeading>2.1 Account Information</SubHeading>
    <BulletList items={[
      'Name, email address, and phone number',
      'Professional details (medical specialism)',
      'Account credentials (passwords are stored in hashed form only)',
    ]} />
    <SubHeading>2.2 Uploaded Content</SubHeading>
    <BulletList items={[
      'Medical documents, clinical records, and associated files',
      'Patient data contained within uploaded documents',
      'Images and supporting materials',
    ]} />
    <SubHeading>2.3 Usage Data</SubHeading>
    <BulletList items={[
      'IP addresses and browser information',
      'Feature usage and interaction patterns',
      'Consent records (type, version, timestamp, IP address)',
    ]} />

    <SectionHeading>3. Special Category Data</SectionHeading>
    <P>
      The Service processes health-related data, which constitutes special category data under
      Article 9 of the UK GDPR. We process this data on the basis of your explicit consent
      (Article 9(2)(a)) and, where applicable, for reasons of substantial public interest in
      the area of public health (Article 9(2)(i)).
    </P>
    <P>
      We implement additional safeguards for special category data, including encryption at rest
      and in transit, strict access controls, and audit logging.
    </P>

    <SectionHeading>4. How We Use Your Data</SectionHeading>
    <BulletList items={[
      'To provide and operate the Service, including AI-powered document analysis and chronology generation.',
      'To manage your account and subscription.',
      'To communicate with you about your account, service updates, and support requests.',
      'To maintain security and prevent fraud.',
      'To comply with legal obligations.',
      'To improve the Service (using aggregated, anonymised data only).',
    ]} />

    <SectionHeading>5. Legal Bases for Processing</SectionHeading>
    <BulletList items={[
      'Consent (Article 6(1)(a)) — for AI processing of uploaded documents and special category data.',
      'Contract (Article 6(1)(b)) — to provide the Service under your subscription agreement.',
      'Legal obligation (Article 6(1)(c)) — to comply with applicable laws and regulations.',
      'Legitimate interests (Article 6(1)(f)) — for service security, fraud prevention, and service improvement.',
    ]} />

    <SectionHeading>6. AI Processing</SectionHeading>
    <P>
      When you upload documents to the Service, they are processed using artificial intelligence
      models to extract information, generate chronologies, and perform evidence analysis. By
      accepting this Privacy Policy, you consent to this AI-powered processing of your uploaded
      Content as described in this section.
    </P>
    <P>
      AI processing is used to assist your professional work, not to make automated decisions
      about individuals. All AI-generated outputs require human review before use.
    </P>
    <P>
      We do not use your uploaded documents or personal data to train AI models. Document processing
      is performed solely for the purpose of providing the Service to you.
    </P>

    <SectionHeading>7. Data Sharing and Third Parties</SectionHeading>
    <P>We may share your data with the following categories of recipients:</P>
    <BulletList items={[
      'Cloud infrastructure providers (for hosting, storage, and AI-powered document processing)',
      'Payment processors (Stripe) for subscription management',
      'Professional advisers (legal, accounting) where required',
      'Law enforcement or regulators where required by law',
    ]} />
    <P>
      We ensure all third-party processors have appropriate data processing agreements in place
      and provide adequate levels of data protection.
    </P>

    <SectionHeading>8. Data Location</SectionHeading>
    <P>
      All personal data and uploaded Content is processed and stored within the United Kingdom.
      We do not transfer your data outside the UK.
    </P>

    <SectionHeading>9. Data Retention</SectionHeading>
    <P>
      We retain your personal data for as long as your account is active. When your subscription
      ends, is cancelled, or your free credits are exhausted without an active subscription, we
      retain your uploaded Content and account data for ninety (90) days (the "Retention Period").
      During the Retention Period, you may continue to access and export your data.
    </P>
    <P>
      After the Retention Period, we reserve the right to permanently delete all Content and
      personal data associated with your account, unless a longer retention period is required by
      law or for the establishment, exercise, or defence of legal claims.
    </P>
    <P>
      We will notify you by email at thirty (30) days and seven (7) days before the end of
      the Retention Period.
    </P>
    <SubHeading>9.1 AI Processing Data Lifecycle</SubHeading>
    <P>
      Certain data generated during AI processing — including document embeddings, vector
      representations, and AI assistant conversation history — is retained for ninety (90) days
      from the date on which the initial analysis for a given case is generated (the "AI
      Availability Window"). After this period, such data may be permanently deleted.
    </P>
    <P>
      Your uploaded documents, extracted text, user-edited content (such as chronology corrections
      and key findings), and completed reports are not affected by the AI Availability Window and
      are retained in accordance with the general retention terms above.
    </P>
    <P>Consent records are retained for 7 years for GDPR compliance audit purposes.</P>

    <SectionHeading>10. Your Rights Under GDPR</SectionHeading>
    <P>You have the following rights regarding your personal data:</P>
    <BulletList items={[
      'Right of access — request a copy of your personal data.',
      'Right to rectification — request correction of inaccurate data.',
      'Right to erasure — request deletion of your data ("right to be forgotten").',
      'Right to restrict processing — request limitation of how we process your data.',
      'Right to data portability — receive your data in a structured, machine-readable format.',
      'Right to object — object to processing based on legitimate interests.',
      'Right to withdraw consent — withdraw consent at any time without affecting the lawfulness of prior processing.',
      'Right not to be subject to automated decision-making — request human review of automated decisions.',
    ]} />
    <P>
      To exercise any of these rights, contact us at <strong>support@clinlexis.co.uk</strong>.
      We will respond to your request within one month.
    </P>

    <SectionHeading>11. Data Security</SectionHeading>
    <P>
      We implement appropriate technical and organisational measures to protect your data, including:
    </P>
    <BulletList items={[
      'Encryption in transit (TLS 1.2+) and at rest (AES-256)',
      'Role-based access controls and authentication',
      'Regular security assessments and penetration testing',
      'Audit logging of all data access and modifications',
      'Secure infrastructure with redundancy and disaster recovery',
    ]} />

    <SectionHeading>12. Data Breach Notification</SectionHeading>
    <P>
      In the event of a personal data breach, we will notify the Information Commissioner's Office
      (ICO) within 72 hours where feasible, as required by Article 33 of the UK GDPR. Where the
      breach is likely to result in a high risk to your rights and freedoms, we will also notify
      you directly without undue delay.
    </P>

    <SectionHeading>13. Cookies</SectionHeading>
    <P>
      The Service uses essential cookies for authentication and session management. We do not use
      third-party tracking cookies. For more information, see our Cookie Policy.
    </P>

    <SectionHeading>14. Children's Privacy</SectionHeading>
    <P>
      The Service is not intended for use by individuals under the age of 18. We do not knowingly
      collect personal data from children.
    </P>

    <SectionHeading>15. Changes to This Policy</SectionHeading>
    <P>
      We may update this Privacy Policy from time to time. We will notify you of material changes
      via email or through the Platform at least 30 days before they take effect. The "Effective Date"
      at the top of this policy indicates when it was last updated.
    </P>

    <SectionHeading>16. Complaints</SectionHeading>
    <P>
      If you are unhappy with how we handle your personal data, you have the right to lodge a
      complaint with the Information Commissioner's Office (ICO):
    </P>
    <P>
      Information Commissioner's Office<br />
      Wycliffe House, Water Lane, Wilmslow, Cheshire, SK9 5AF<br />
      Website: <strong>ico.org.uk</strong> &nbsp;|&nbsp; Helpline: 0303 123 1113
    </P>

    <SectionHeading>17. Contact</SectionHeading>
    <P>
      For questions about this Privacy Policy or to exercise your data rights, contact us at{' '}
      <strong>support@clinlexis.co.uk</strong> or write to our Data Protection Officer at
      the address above.
    </P>
  </div>
);
